export type SearchParamsLike = {
  delete: (key: string) => void;
  forEach: (callback: (value: string, key: string) => void) => void;
  get: (key: string) => string | null;
  has: (key: string) => boolean;
  set: (key: string, value: string) => void;
  toString: () => string;
};

function decode(value: string) {
  try {
    return decodeURIComponent(value.replace(/\+/g, ' '));
  } catch {
    return value;
  }
}

function encode(value: string) {
  return encodeURIComponent(value);
}

export class SearchParams implements SearchParamsLike {
  private values: Array<[string, string]> = [];

  constructor(input?: string | SearchParamsLike | null) {
    const query =
      typeof input === 'string'
        ? input
        : input && typeof input.toString === 'function'
        ? input.toString()
        : '';

    query
      .replace(/^\?/, '')
      .split('&')
      .filter(Boolean)
      .forEach((part) => {
        const separatorIndex = part.indexOf('=');
        const key =
          separatorIndex >= 0 ? part.slice(0, separatorIndex) : part;
        const value = separatorIndex >= 0 ? part.slice(separatorIndex + 1) : '';

        this.values.push([decode(key), decode(value)]);
      });
  }

  delete(key: string) {
    this.values = this.values.filter(([paramKey]) => paramKey !== key);
  }

  forEach(callback: (value: string, key: string) => void) {
    this.values.forEach(([key, value]) => callback(value, key));
  }

  get(key: string) {
    const entry = this.values.find(([paramKey]) => paramKey === key);
    return entry ? entry[1] : null;
  }

  has(key: string) {
    return this.values.some(([paramKey]) => paramKey === key);
  }

  set(key: string, value: string) {
    this.delete(key);
    this.values.push([key, value]);
  }

  toString() {
    return this.values
      .map(([key, value]) => `${encode(key)}=${encode(value)}`)
      .join('&');
  }
}

export function createSearchParams(input?: string | SearchParamsLike | null) {
  return new SearchParams(input);
}
